/**
 * Geometry checks for a contour being drawn, with the offending points named so the map can
 * mark them.
 *
 * Self-intersection is the one that matters. A bow-tie polygon still draws, still saves and
 * still reports an area — a WRONG area, because the two lobes cancel in the shoelace sum.
 * Everything downstream of a field is area-based: the 500 ha cost guard, the small-field
 * warning, the GEE budget. A silent bow-tie is therefore not a cosmetic problem, it is a
 * wrong number the operator has no way to see.
 *
 * Geoman can refuse a self-intersecting shape outright (`allowSelfIntersection: false`), but
 * a refusal says only "no" and points at nothing — and it refuses the GESTURE, so the person
 * never learns what was wrong. This lets the ring be drawn and returns the crossings.
 */

import type { GeoJsonPolygon } from "../api/types";
import { T } from "../strings";

export type GeometryIssueKind =
  "self_intersection" | "duplicate_points" | "too_few_points" | "out_of_range";

export interface GeometryIssue {
  kind: GeometryIssueKind;
  message: string;
  /** [lon, lat], map order. */
  points: [number, number][];
}

export interface GeometryCheck {
  valid: boolean;
  issues: GeometryIssue[];
  /** Everything worth marking on the map, deduplicated. */
  points: [number, number][];
}

type Ring = number[][];
type Segment = [[number, number], [number, number]];

/**
 * Hand-drawn contours are tens of vertices, so the O(n²) sweep is free. The cap only exists
 * so that an imported contour of a river basin cannot freeze the tab; past it we say nothing
 * rather than claim the geometry is clean.
 */
const MAX_VERTICES = 2_000;

const isPoint = (value: number[] | undefined): value is [number, number] =>
  Array.isArray(value) && typeof value[0] === "number" && typeof value[1] === "number";

/**
 * Proper crossing of two segments. Endpoints count: two non-adjacent edges that merely touch
 * still pinch the ring, and the shoelace area is wrong from that point on.
 */
const crossing = (a: Segment, b: Segment): [number, number] | null => {
  const [[ax1, ay1], [ax2, ay2]] = a;
  const [[bx1, by1], [bx2, by2]] = b;
  const denominator = (ax2 - ax1) * (by2 - by1) - (ay2 - ay1) * (bx2 - bx1);
  // Parallel or collinear: an overlap here is a duplicate-point / degenerate case, reported
  // by its own check rather than as a phantom single crossing point.
  if (denominator === 0) return null;
  const t = ((bx1 - ax1) * (by2 - by1) - (by1 - ay1) * (bx2 - bx1)) / denominator;
  const u = ((bx1 - ax1) * (ay2 - ay1) - (by1 - ay1) * (ax2 - ax1)) / denominator;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return [ax1 + t * (ax2 - ax1), ay1 + t * (ay2 - ay1)];
};

const segmentsOf = (ring: Ring): Segment[] => {
  // Repeated neighbours give zero-length edges, and a zero-length edge sits on top of its
  // neighbours — every one of them would be reported as a crossing. Duplicates have their
  // own check; here they are simply not edges.
  const points: [number, number][] = [];
  for (const value of ring) {
    if (!isPoint(value)) continue;
    const previous = points[points.length - 1];
    if (previous && previous[0] === value[0] && previous[1] === value[1]) continue;
    points.push(value);
  }
  const first = points[0];
  const last = points[points.length - 1];
  const closed =
    points.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]
      ? points.slice(0, -1)
      : points;
  if (closed.length < 3) return [];
  return closed.map((point, index) => [point, closed[(index + 1) % closed.length]!] as Segment);
};

const selfIntersections = (ring: Ring): [number, number][] => {
  const segments = segmentsOf(ring);
  if (segments.length < 4 || segments.length > MAX_VERTICES) return [];
  const found: [number, number][] = [];
  for (let i = 0; i < segments.length; i += 1) {
    // Adjacent edges share a vertex by construction; the last and the first do too.
    for (let j = i + 2; j < segments.length; j += 1) {
      if (i === 0 && j === segments.length - 1) continue;
      const point = crossing(segments[i]!, segments[j]!);
      if (point) found.push(point);
    }
  }
  return found;
};

const ringsOf = (geometry: GeoJsonPolygon): Ring[] =>
  geometry.type === "Polygon"
    ? (geometry.coordinates as Ring[])
    : (geometry.coordinates as Ring[][]).flat();

const key = (point: [number, number]): string => `${point[0]},${point[1]}`;

export const checkGeometry = (geometry: GeoJsonPolygon | null | undefined): GeometryCheck => {
  if (!geometry) return { valid: true, issues: [], points: [] };
  const rings = ringsOf(geometry);

  const crossings: [number, number][] = [];
  const duplicates: [number, number][] = [];
  const outOfRange: [number, number][] = [];
  const sparse: [number, number][] = [];

  for (const ring of rings) {
    crossings.push(...selfIntersections(ring));

    for (let index = 1; index < ring.length; index += 1) {
      const previous = ring[index - 1];
      const point = ring[index];
      if (!isPoint(previous) || !isPoint(point)) continue;
      if (previous[0] === point[0] && previous[1] === point[1]) duplicates.push(point);
    }

    const unique = new Set(ring.filter(isPoint).map((point) => key(point as [number, number])));
    if (unique.size < 3) {
      const first = ring.find(isPoint);
      if (first) sparse.push(first);
    }

    for (const point of ring) {
      if (!isPoint(point)) continue;
      const [lon, lat] = point;
      if (lon < -180 || lon > 180 || lat < -90 || lat > 90) outOfRange.push(point);
    }
  }

  const issues: GeometryIssue[] = [];
  // Order is the order of the operator's attention: the wrong-area bug first.
  if (crossings.length > 0) {
    issues.push({
      kind: "self_intersection",
      message: T.draw.validation.selfIntersection,
      points: crossings,
    });
  }
  if (outOfRange.length > 0) {
    issues.push({
      kind: "out_of_range",
      message: T.draw.validation.outOfRange,
      points: outOfRange,
    });
  }
  if (duplicates.length > 0) {
    issues.push({
      kind: "duplicate_points",
      message: T.draw.validation.duplicatePoints,
      points: duplicates,
    });
  }
  if (sparse.length > 0) {
    issues.push({
      kind: "too_few_points",
      message: T.draw.validation.tooFewPoints,
      points: sparse,
    });
  }

  // A half-drawn ring is not marked: the missing vertices are not AT any coordinate, and a
  // dot on the first one only says "here is where you started".
  const marked = new Map<string, [number, number]>();
  for (const issue of issues) {
    if (issue.kind === "too_few_points") continue;
    for (const point of issue.points) marked.set(key(point), point);
  }

  return { valid: issues.length === 0, issues, points: [...marked.values()] };
};
